import React from 'react';
import { format } from 'date-fns';
import { COLOR_DICTIONARY } from '@sp-types/colors';
import { getDate } from '../../FinancialPositions/components/FinancialPositionsUtils';

import { formatToPriceInDollars, formatToShortString } from '../../../utils/FormatValue';
import { IHistoryPrice, IRoute } from '../../../sp-types';
import { CurveMiniChart } from './CurveMiniChart';

export const HistoricalViewOptions = [
  { text: '1 year', value: 12 },
  { text: '2 years', value: 24 },
  { text: '5 years', value: 60 },
];

export interface PricePoint {
  key?: any;
  name: string;
  price?: number;
  future?: number;
  color: string;
  id: string;
  visible: boolean;
}

export interface IndexRoutes {
  [x: string]: any;
  date: string;
  price?: string;
  future?: number;
  cost?: string;
  route1?: string;
  route2?: string;
  route3?: string;
}

interface KeyPointsProps {
  routePrices: PricePoint[];
  selectedRoute: string;
  currency?: string;
}

export const dateAddMonth = (currDate: Date, addMonths: number): Date => {
  return new Date(
    currDate.getFullYear() + Math.floor((currDate.getMonth() + addMonths) / 12),
    (currDate.getMonth() + addMonths) % 12,
    15,
  );
};

export const priceObjectToFutures = (historyPrice: IHistoryPrice) => {
  const baseDate = new Date(historyPrice.date);

  return Array.from({ length: 12 })
    .map((_, i) => {
      const monthKey = `month${(i + 1).toString().padStart(2, '0')}`;

      return monthKey in historyPrice
        ? {
            date: format(dateAddMonth(baseDate, i + 1), 'yyyy-MM-dd'),
            price: historyPrice.price,
            future: historyPrice[monthKey as keyof IHistoryPrice],
            isFuture: true,
          }
        : null;
    })
    .filter((item) => item !== null);
};

export const getCurveForIndex = (index: number, date?: Date, pricesArr?: any[]): PricePoint[] =>
  index > 0 && pricesArr?.[index] && date
    ? Object.entries(pricesArr[index])
        .filter(([key]) => key.startsWith('month'))
        .sort((a, b) => +a[0].replace('month', '') - +b[0].replace('month', ''))
        .map(([key, val], i) => ({
          name: format(getDate(date, +key.replace('month', '')), 'MMM yy`'),
          price: val as number,
          color: COLOR_DICTIONARY[i % COLOR_DICTIONARY.length],
          id: `index ${i}`,
          key,
          visible: false,
        }))
    : [];

const cost = { DELTA: 20, INT_RATE: 0.0025 };

export const isSameMonth = (dateA: Date, dateB: Date): boolean =>
  dateA.getFullYear() === dateB.getFullYear() && dateA.getMonth() === dateB.getMonth();

export const addFuturePriceRoute = (lastPrice: IndexRoutes, showCount: number): IndexRoutes[] =>
  Array.from({ length: showCount }).map((_, i) => ({
    date: dateAddMonth(new Date(), i).toLocaleDateString('en-CA'),
    futurePrice: lastPrice.price,
    cost: (
      +(lastPrice.price || '0') +
      cost.DELTA +
      i * cost.INT_RATE * (+(lastPrice.price || '0') + cost.DELTA)
    ).toString(),
  }));

export const KeyPoints: React.FC<KeyPointsProps> = ({ routePrices, selectedRoute, currency = '$' }) => (
  <div className="flex flex-col text-sm min-w-40 gap-3 h-full">
    <div className="flex flex-col">
      <p className="text-red text-lg p-0">Values</p>
    </div>

    {/* Scrollable key points section */}
    <div className="flex flex-col text-sm min-w-44 gap-2 overflow-y-auto flex-1">
      {routePrices
        ?.filter(({ visible, price }) => visible && price)
        .map(({ name, price, color, id }, i) => (
          <div
            key={i}
            className="flex w-full flex-col gap-1 ps-2 border-l-4 flex-shrink-0"
            style={{ borderLeftColor: color, borderLeftWidth: selectedRoute === id ? '6px' : '4px' }}
          >
            <p className={id === selectedRoute ? 'text-black font-bold text-nowrap' : 'text-gray-450'}>{name}</p>
            <p className="p-0 text-lg">{currency + formatToShortString(+price)}</p>
          </div>
        ))}
      {routePrices?.some((a) => !a.visible) && (
        <div className="curve border-1 border-gray-300 p-2 flex flex-col gap-2">
          {routePrices
            ?.filter((a) => !a.visible)
            .map(({ name, price }, i) => (
              <div key={i} className="flex gap-2 text-sm">
                <p className="w-24">{name}</p>
                <p>{currency + formatToShortString(+price)}</p>
              </div>
            ))}
          <div className="flex-shrink-0 mt-auto">
            <CurveMiniChart routePrices={routePrices.filter((r) => r.id.startsWith('index'))} currency={currency} />
          </div>
        </div>
      )}
    </div>
  </div>
);
/*
  <div className="flex flex-col text-sm min-w-40 gap-3">
    <div className="flex flex-col">
      <p className="text-red  text-lg p-0">Values</p>
    </div>
    <div className="flex flex-col text-sm min-w-40 gap-3">
      {routePrices
        ?.filter(({ visible, price }) => visible && price)
        .map(({ name, price, color, id }, i) => (
          <div
            key={i}
            className="flex w-full flex-col gap-1 ps-2 border-l-4"
            style={{ borderLeftColor: color, borderLeftWidth: selectedRoute === id ? '6px' : '4px' }}
          >
            <p className={id === selectedRoute ? 'text-black font-bold text-nowrap' : 'text-gray-450'}>{name}</p>
            <p className="p-0 text-lg">{formatToPriceInDollars(+price)}</p>
          </div>
        ))}
    </div>
    <div className="flex flex-1">
    </div>
  </div>
*/

export const getRoutePrice = (price: IndexRoutes, routeId: string): string => price[routeId] || 0;

export const RouteExtraInfo: React.FC<{
  pointPrices?: IndexRoutes;
  currentPrice: IndexRoutes;
  routeId?: string;
  route?: IRoute;
}> = ({ pointPrices, currentPrice, route }) => {
  const futurePoint = pointPrices?.[route?.id || ''] || '';

  // const track = currRoutePrice - +(currentPrice.price || '0');
  // const period = +(routePoint?.price || '') - currRoutePrice;
  const diff = futurePoint && currentPrice?.price ? futurePoint - currentPrice.price : 0;

  return (
    <div className="border-l border-l-gray-200 flex min-w-64 p-1 ps-3 gap-3">
      <div className="flex flex-col p-1 gap-2 min-w-28">
        <p className="text-gray-450 text-sm">Date</p>
        <p>{pointPrices?.date ? format(pointPrices.date, 'MMM yyyy') : '-'}</p>
      </div>
      {/* <div className="flex flex-col p-1 gap-2 min-w-20">
        <p className="text-gray-450 text-sm">C/B</p>
        <p>{formatToPriceInDollars(route?.contago || period)}</p>
      </div> */}
      <div className="flex flex-col p-01 gap-2 min-w-28">
        <p className="text-gray-450 text-sm">Difference</p>
        <b className={`text-lg ${diff > 0 ? 'text-green-300' : 'text-red'}`}>
          {diff ? formatToPriceInDollars(diff) : '-'}
        </b>
      </div>
    </div>
  );
};
